import React, { Component } from 'react';
import isEqual from 'lodash/isEqual';
import { createSelector } from 'reselect';
import { connect } from 'react-redux';
import { MoreDropDown, Export, SetDefaultLandingPageComp } from '@components/common';
import {
  DASHBOARD_TYPE,
  DASHBOARD_URL,
  EXPORT_TRENDS,
  NOC_DRILLDOWN,
  isAppModule,
} from '@utils/constants';
import track, {
  CLICK_EXPORT_TRENDS,
  CLICK_ON_DELETE_DASHBOARD,
  CLICK_ON_UPDATE_DASHBOARD,
  CLICK_SAVE_FROM_KEBAB,
  CLICK_SHARE_FROM_KEBAB,
} from '@src/utils/sensor/track';
import { exportData } from '@store/export/actions';
import { setActiveDashboardId } from '@store/dashboards/ui/actions';
import { selector as defaultLandingPageSelector } from '@store/defaultLandingPage';
import {
  setDefaultLandingPage,
  setDefaultLandingPagePromptNeedInput,
  setDefaultLandingPagePromptTitle,
  setDefaultLandingPagePromptContent,
  setDefaultLandingPagePromptOkFn,
  showPrompt,
} from '@store/defaultLandingPage/actions';
import commonSelector, {
  annotationsFeatureSelector,
  horizontalLayoutSelector,
  realTimeModeOnTrendsSelector,
  publicSavedDashboardsSelector,
} from '@store/common/selectors';
import Latch from '@components/common/Latch';
import useShareSet from '@components/share/useShareSet';
import { SHARE_SERVICE_DASHBOARD_KEYS, trendsShareConfig } from '@components/share/utils';
import Share from '@components/share/Share';
import SavingWrapper from '@components/saving/SavingWrapper/SavingWrapper';
import useSaveSet, { SavingStateObjectType } from '@components/saving/useSaveSet';
import DeleteSavedDashboardWrapper from '@components/saving/DeleteSavedDashboardWrapper/DeleteSavedDashboardWrapper';
import { modeSelector } from '@store/trends/selectors';
import featureNames from '@utils/featureToggle/constants';
import { MODE_ALERT_DIAGNOSTICS, MODE_AI_ALERT_DIAGNOSTICS } from '@store/trends/constants';
import DefaultLandingPrompt from '@components/common/DefaultLandingPage';
import getActualShareLink from '../../common/Sharing/SharingModal/SharingContent/getActualShareLink';
import { alertSelector } from '@store/trends/alerts/selectors';
import { aiAlertSelector } from '@store/trends/aiAlerts/selectors';
import styles from './index.less';
import AnnotationToggle from './AnnotationToggle';
import ExportOption from './ExportOption';

const { baseUrl } = window.conviva;

class OptionsDropdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dlpTitle: '',
      dlpContent: '',
      dlpOkFn: '',
      dlpNeedInput: '',
    };
  }

  shouldComponentUpdate(nextProps) {
    const { sharingState, defaultLandingPage, showMask }: any = this.props;
    return (
      !isEqual(nextProps.sharingState, sharingState) ||
      !isEqual(nextProps.defaultLandingPage.url, defaultLandingPage.url) ||
      !isEqual(nextProps.showMask, showMask)
    );
  }

  onExport = (context) => {
    const { exportData: dispatchExportData }: any = this.props;
    dispatchExportData(context);
  };

  onSetDefaultLandingApply = () => {
    const {
      cid,
      setDefaultLandingPage: dispatchSetDefaultLandingPage,
      mode,
      alert,
    }: any = this.props;
    const { pathname } = window.location;
    const arr = pathname.split('/');
    const u = getActualShareLink(pathname, cid);
    if (mode === MODE_ALERT_DIAGNOSTICS) {
      dispatchSetDefaultLandingPage({ url: u, name: `Alert: ${alert.name}`, path: pathname });
    } else {
      dispatchSetDefaultLandingPage({ url: u, name: arr[arr.length - 1], path: pathname });
    }
  };

  onSetDefaultLanding = () => {
    const { defaultLandingPage }: any = this.props;
    const {
      setActiveDashboardId: dispatchSetActiveDashboardId,
      setDefaultLandingPagePromptNeedInput: dispatchSetDefaultLandingPagePromptNeedInput,
      setDefaultLandingPagePromptTitle: dispatchSetDefaultLandingPagePromptTitle,
      setDefaultLandingPagePromptContent: dispatchSetDefaultLandingPagePromptContent,
      setDefaultLandingPagePromptOkFn: dispatchSetDefaultLandingPagePromptOkFn,
      showPrompt: showDefaultLandingPrompt,
    }: any = this.props;
    const hasDefaultLandingPage = defaultLandingPage.url.length > 0;
    const dlpContent = hasDefaultLandingPage
      ? 'Are you sure you want to change your default dashboard?'
      : 'Are you sure you want to set your default dashboard?';
    const obj = {
      dlpTitle: 'Make default',
      dlpContent,
      dlpOkFn: () => this.onSetDefaultLandingApply(),
      dlpNeedInput: false,
    };
    dispatchSetActiveDashboardId(null);
    dispatchSetDefaultLandingPagePromptNeedInput(obj.dlpNeedInput);
    dispatchSetDefaultLandingPagePromptTitle(obj.dlpTitle);
    dispatchSetDefaultLandingPagePromptContent(obj.dlpContent);
    dispatchSetDefaultLandingPagePromptOkFn(obj.dlpOkFn);
    this.setState(obj);
    showDefaultLandingPrompt();
  };

  render() {
    const {
      sharingState,
      savingState,
      share,
      features,
      defaultLandingPage,
      showMask,
      isSharing,
      mode,
      id,
      liveModeSelected,
      horizontalLayout,
      annotationsFeature,
      pageKeyForSaveService,
      isSavedDashboard,
      profileId,
      savedDashboardCreatorId,
      publicSavedDashboardsFeature,
    }: any = this.props;

    const showShareOption = share && !liveModeSelected && !isAppModule;
    const showExport = features[featureNames.EXPORT_DATA] && !liveModeSelected && !horizontalLayout;
    const setDefaultLandingFlag = features[featureNames.DEFAULT_LANDING_PAGE] || true;
    const { dlpTitle, dlpContent, dlpNeedInput, dlpOkFn }: any = this.state;
    const { pathname } = window.location;
    const defaultPathname = defaultLandingPage.url || '';
    const isDefaultLandingPage = defaultPathname.endsWith(pathname);
    const hideMakeDefaultOption =
      isSharing || mode === NOC_DRILLDOWN || mode === MODE_AI_ALERT_DIAGNOSTICS;

    const showNewExport = horizontalLayout;

    const shareUrl =
      mode === MODE_AI_ALERT_DIAGNOSTICS
        ? 'dashboard/ai-alerts/diagnostics/sharing'
        : 'dashboard/trends/sharing';

    const savedDashboardUrl = DASHBOARD_URL[DASHBOARD_TYPE.TRENDS].slice(0, -1);

    const shouldShowSaveOption = () => {
      return (
        horizontalLayout &&
        publicSavedDashboardsFeature &&
        !liveModeSelected &&
        // show save option if not a saved dashboard,
        // or show update option if it's a saved dashboard, and creatorId matches current profileId
        (!isSavedDashboard || savedDashboardCreatorId === profileId)
      );
    };

    const shouldShowDeleteOption = () => {
      return (
        isSavedDashboard &&
        publicSavedDashboardsFeature &&
        !isAppModule &&
        savedDashboardCreatorId === profileId
      );
    };

    const onShareClick = () => {
      track({ event: CLICK_SHARE_FROM_KEBAB });
    };

    const onSaveClick = () => {
      track({ event: CLICK_SAVE_FROM_KEBAB });
    };

    const onUpdateClick = () => {
      track({ event: CLICK_ON_UPDATE_DASHBOARD });
    };

    const onDeleteClick = () => {
      track({ event: CLICK_ON_DELETE_DASHBOARD });
    };

    const saveData: SavingStateObjectType = {
      meta: {
        dashboardTypeId: DASHBOARD_TYPE.TRENDS,
      },
      config: savingState,
    };

    return (
      <div className={styles.options}>
        {!isAppModule && (
          <>
            <Latch
              useHook={useShareSet}
              params={{ pageKey: SHARE_SERVICE_DASHBOARD_KEYS.TRENDS, state: sharingState }}
            />
            <Latch
              useHook={useSaveSet}
              params={{
                pageKey: pageKeyForSaveService,
                saveData,
              }}
            />
          </>
        )}
        <MoreDropDown>
          {annotationsFeature && <AnnotationToggle />}
          {showShareOption && (
            <div onClick={onShareClick}>
              <Share
                config={{
                  ...trendsShareConfig,
                  url: `${baseUrl}/${shareUrl}`,
                  savedDashboardUrl: `${savedDashboardUrl}`,
                }}
                pageKeyForSaveService={pageKeyForSaveService}
                id={id}
              />
            </div>
          )}
          {shouldShowSaveOption() && (
            <SavingWrapper
              pageMeta={{
                page: {
                  key: pageKeyForSaveService,
                },
              }}
            >
              {isSavedDashboard ? (
                <div onClick={onUpdateClick}>Update</div>
              ) : (
                <div onClick={onSaveClick}>Save</div>
              )}
            </SavingWrapper>
          )}
          {shouldShowDeleteOption() && (
            <DeleteSavedDashboardWrapper
              pageMeta={{
                id,
              }}
              dashboardTypeName={isAppModule ? 'App_Experience' : 'TRENDS'}
            >
              <div onClick={onDeleteClick}>Delete</div>
            </DeleteSavedDashboardWrapper>
          )}
          {showExport && (
            <Export
              context={EXPORT_TRENDS}
              clickCallback={this.onExport}
              showIcon={false}
              trackEvent={{ event: CLICK_EXPORT_TRENDS }}
              disabled={showMask}
            />
          )}
          {showNewExport && <ExportOption />}
          {setDefaultLandingFlag && !hideMakeDefaultOption && (
            <SetDefaultLandingPageComp
              context={null}
              clickCallback={this.onSetDefaultLanding}
              disabled={isDefaultLandingPage}
            />
          )}
        </MoreDropDown>
        <DefaultLandingPrompt
          title={dlpTitle}
          content={dlpContent}
          onOkFn={dlpOkFn}
          needInput={dlpNeedInput}
        />
      </div>
    );
  }
}

const selector = createSelector(
  commonSelector,
  defaultLandingPageSelector,
  modeSelector,
  alertSelector,
  aiAlertSelector,
  realTimeModeOnTrendsSelector,
  horizontalLayoutSelector,
  annotationsFeatureSelector,
  publicSavedDashboardsSelector,
  (
    commonStore,
    defaultLandingPage,
    modeStore,
    alertStore,
    aiAlertStore,
    liveModeSelected,
    horizontalLayout,
    annotationsFeature,
    publicSavedDashboardsFeature,
  ) => ({
    features: commonStore.features,
    defaultLandingPage,
    cid: commonStore.profiles.filter((p) => p.is_selected)[0].customer_id,
    profileId: commonStore.profiles.filter((p) => p.is_selected)[0].profile_id,
    showMask: commonStore.showMask,
    mode: modeStore.mode,
    alert: alertStore.alert,
    aiAlert: aiAlertStore,
    isSharing: commonStore.isSharingPage,
    liveModeSelected,
    horizontalLayout,
    annotationsFeature,
    publicSavedDashboardsFeature,
  }),
);

export default connect(selector, {
  setActiveDashboardId,
  exportData,
  setDefaultLandingPage,
  setDefaultLandingPagePromptNeedInput,
  setDefaultLandingPagePromptTitle,
  setDefaultLandingPagePromptContent,
  setDefaultLandingPagePromptOkFn,
  showPrompt,
})(OptionsDropdown);
